import { useEffect, useState } from 'react';
import api from '../lib/api';
import VoiceMessage from './VoiceMessage';
import { Image as ImageIcon, Mic, MapPin } from 'lucide-react';

/**
 * GroupMediaTab — shared media gallery (photos, voice notes, locations) inside
 * Group Info. Photos go in a 3-col grid; voice notes are stacked rows.
 */
export default function GroupMediaTab({ groupId, currentUserId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/groups/${groupId}/media`);
        setItems(Array.isArray(data) ? data : []);
      } catch {/* ignore */}
      setLoading(false);
    })();
  }, [groupId]);

  if (loading) {
    return <p className="text-center text-xs text-[#9B9EA3] py-6">Loading media…</p>;
  }
  if (!items.length) {
    return <p className="text-center text-xs text-[#9B9EA3] py-6">No media shared yet.</p>;
  }

  const photos = items.filter(m => m.media_type === 'image' && m.media_url);
  const videos = items.filter(m => m.media_type === 'video' && m.media_url);
  const audios = items.filter(m => m.audio_url);

  return (
    <div className="space-y-4">
      {(photos.length > 0 || videos.length > 0) && (
        <div data-testid="media-photos">
          <p className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Photos &amp; videos</p>
          <div className="grid grid-cols-3 gap-1">
            {[...photos, ...videos].map(m => (
              <a
                key={m.id}
                href={m.media_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`media-tile-${m.id}`}
                className="relative aspect-square overflow-hidden bg-[#F1F0EA] rounded-md"
              >
                {m.media_type === 'video' ? (
                  <video src={m.media_url} className="w-full h-full object-cover" />
                ) : (
                  <img decoding="async" src={m.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
                {m.media_type === 'video' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-[#111111]">▶</span>
                    </div>
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}
      {audios.length > 0 && (
        <div data-testid="media-voice">
          <p className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Voice notes</p>
          <div className="space-y-2">
            {audios.map(m => (
              <div key={m.id} className="bg-[#F1F0EA] rounded-2xl px-3 py-2 flex items-center gap-2.5" data-testid={`media-voice-${m.id}`}>
                <Mic size={14} className="text-[#3F5200] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-[#111111] truncate">{m.user?.display_name || 'Voice note'}</p>
                  <VoiceMessage url={m.audio_url} duration={m.audio_duration} isMine={false} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
